import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { EditorView } from '@tiptap/pm/view';
import { store } from '@/state/store';
import { PAGE_DIMENSIONS } from '@/state/types';
import type { PageSetup } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// Živé stránkování (Fáze 2, varianta B) — vizuální A4 stránky přímo v editoru.
//
// PŘÍSTUP: dekorační vrstva nad plynulým dokumentem. Dokument (ProseMirror
// model) zůstává JEDEN souvislý tok — editace, undo, kopírování, DOCX export
// se nemění a nemůžou se „rozbít" špatným stránkováním. Nad ním běží tento
// plugin, který:
//   1. změří svislé pozice top-level bloků (odstavce, nadpisy, tabulky…),
//   2. spočítá, kde by přetekla využitelná výška A4 (výška − okraje − HF),
//   3. na hranici vloží WIDGET dekoraci = vizuální „řez stránky" (konec listu
//      + mezera + začátek dalšího listu). Ruční konec stránky (PageBreak node)
//      vynutí řez vždy.
//
// PLYNULOST: přepočet je debouncovaný (po odmlce v psaní), takže psaní samo
// neběží přes měření. Priorita zadaná uživatelem = plynulost > přesnost zlomu.
//
// Vzhled listů řídí CSS (.pm-paginated, .pm-page-gap). Plugin jen říká KDE
// řez je; jak vypadá, je věc stylů.
// ═══════════════════════════════════════════════════════════════

export const paginationKey = new PluginKey('livePagination');

const PX_PER_MM = 96 / 25.4; // orientační; reálná měření berou z DOM

interface Geometry {
  pageHeightPx: number; // využitelná výška těla na stránce v px (orientační)
  gapMm: number; // vizuální mezera mezi listy
  usableMm: number; // využitelná výška těla v mm (přesná, kalibruje se na px)
}

/** Spočítá využitelnou výšku těla stránky (bez okrajů a HF zóny) v px. */
function computeGeometry(ps: PageSetup): Geometry {
  const dims = PAGE_DIMENSIONS[ps.size];
  const landscape = ps.orientation === 'landscape';
  const hmm = landscape ? dims.w : dims.h;
  // Rezerva na záhlaví/zápatí, když existují (musí ladit s applyPageSetup).
  const s = store.get();
  const hasHeader = !!s.doc.headerHtml?.trim();
  const hasFooter = !!s.doc.footerHtml?.trim() || !!(s.doc.pageNumber && s.doc.pageNumber.enabled);
  const hfReserveTop = hasHeader ? 8 : 0;
  const hfReserveBottom = hasFooter ? 8 : 0;
  const usableMm = hmm - ps.margins.top - ps.margins.bottom - hfReserveTop - hfReserveBottom;
  return {
    pageHeightPx: usableMm * PX_PER_MM,
    gapMm: 0,
    usableMm, // v mm, kalibrace na px proběhne podle skutečné šířky stránky
  };
}

/**
 * Projde top-level bloky, změří jejich skutečnou výšku (VČETNĚ svislých
 * marginů) z DOM a určí pozice řezů. Výška těla stránky se kalibruje na px
 * podle skutečné šířky vykreslené A4 — tím sedí i při zoomu / různém DPI.
 */
interface PageBreakInfo {
  pos: number;
  manual: boolean; // true = ruční konec stránky (pageBreak node), false = přetečení
}

function computeBreaks(view: EditorView, ps: PageSetup): PageBreakInfo[] {
  const geo = computeGeometry(ps);
  if (geo.usableMm <= 0) return [];

  const breaks: PageBreakInfo[] = [];
  const { doc } = view.state;

  const contentEl = view.dom as HTMLElement;
  const calPxPerMm = calibratePxPerMm(contentEl, ps);
  const usablePx = geo.usableMm * calPxPerMm;

  let accumulated = 0;

  doc.forEach((node, offset) => {
    const domAt = view.nodeDOM(offset) as HTMLElement | null;
    let blockH = 0;
    if (domAt && domAt.getBoundingClientRect) {
      const r = domAt.getBoundingClientRect();
      // Zahrň svislé marginy bloku — jinak se na stránku „vejde" víc, než
      // reálně zabírá, a řezy padnou pozdě.
      const cs = window.getComputedStyle(domAt);
      const mt = parseFloat(cs.marginTop) || 0;
      const mb = parseFloat(cs.marginBottom) || 0;
      blockH = r.height + mt + mb;
    }

    // Ruční konec stránky → řez ZA node (aby čára „Konec stránky" zůstala na
    // konci ukončované stránky, ne na začátku další). Reset akumulace.
    if (node.type.name === 'pageBreak') {
      breaks.push({ pos: offset + node.nodeSize, manual: true });
      accumulated = 0;
      return;
    }

    if (accumulated + blockH > usablePx && accumulated > 0) {
      breaks.push({ pos: offset, manual: false });
      accumulated = blockH;
    } else {
      accumulated += blockH;
    }
  });

  return breaks;
}

/** Kalibruje px na mm podle skutečné šířky vykreslené stránky. */
function calibratePxPerMm(contentEl: HTMLElement, ps: PageSetup): number {
  const page = contentEl.closest('.doc-page') as HTMLElement | null;
  if (!page) return PX_PER_MM;
  const dims = PAGE_DIMENSIONS[ps.size];
  const landscape = ps.orientation === 'landscape';
  const wmm = landscape ? dims.h : dims.w;
  const rectW = page.getBoundingClientRect().width;
  if (rectW > 0 && wmm > 0) return rectW / wmm;
  return PX_PER_MM;
}

/**
 * Dorovná KAŽDOU stránku na plnou výšku A4 — nejen poslední. Stránky ukončené
 * řezem (ruční konec stránky i automatické přetečení) se jinak scvrknou na
 * obsah; uživatel ale chce vidět celou A4 vždy.
 *
 * Princip: každý .pm-page-gap dostane dynamickou výšku = základní mezera +
 * kolik chybí PŘEDCHOZÍ stránce do plné A4. Poslední (neukončená) stránka se
 * dorovná spodní výplní contentu (--pg-fill).
 *
 * Měření probíhá ve dvou průchodech: nejdřív vynulujeme dorovnání, změříme
 * skutečné pozice, pak nastavíme výšky — aby staré hodnoty nezkreslovaly.
 */
function applyPageFills(view: EditorView, ps: PageSetup): void {
  const contentEl = view.dom as HTMLElement;
  const page = contentEl.closest('.doc-page') as HTMLElement | null;
  if (!page) return;

  const calPxPerMm = calibratePxPerMm(contentEl, ps);
  if (calPxPerMm <= 0) return;
  const dims = PAGE_DIMENSIONS[ps.size];
  const landscape = ps.orientation === 'landscape';
  const hmm = landscape ? dims.w : dims.h;
  const fullPagePx = hmm * calPxPerMm; // plná výška A4 listu v px
  if (fullPagePx <= 0) return;

  const gaps = Array.from(contentEl.querySelectorAll('.pm-page-gap')) as HTMLElement[];

  // 1) Reset — vynuluj všechny dorovnávací výšky, ať měříme čistý layout.
  const baseGapPx = getBaseGapPx(page); // vizuální mezera mezi listy (z --pg-gap)
  gaps.forEach((g) => { g.style.height = ''; });
  contentEl.style.setProperty('--pg-fill', '0px');

  // Vynutíme reflow čtením.
  void contentEl.scrollHeight;

  const contentTop = contentEl.getBoundingClientRect().top;

  // 2) Projdi stránky shora dolů. Začátek stránky = 0 nebo spodek předchozího
  //    gapu (po jeho dorovnání). Pro každý gap spočítej, kolik chybí jeho
  //    stránce (od začátku stránky po vršek gapu) do plné A4.
  let pageStartY = 0; // Y (vůči contentTop) horní hrany aktuální stránky
  for (const gap of gaps) {
    const gapTopY = gap.getBoundingClientRect().top - contentTop;
    const usedByPage = gapTopY - pageStartY; // výška obsahu na této stránce
    const missing = Math.max(0, fullPagePx - usedByPage); // kolik chybí do A4
    const gapH = Math.round(missing + baseGapPx); // gap = výplň + vizuální mezera
    gap.style.height = gapH + 'px';

    // Po nastavení výšky gapu se posunul layout → přeměř začátek další stránky.
    // Spodek tohoto gapu = start další stránky.
    const gapBottomY = gap.getBoundingClientRect().bottom - contentTop;
    pageStartY = gapBottomY;
  }

  // 3) Poslední (neukončená) stránka — dorovnej spodní výplní contentu.
  const currentH = contentEl.scrollHeight;
  const lastPageUsed = currentH - pageStartY;
  const lastMissing = Math.max(0, Math.round(fullPagePx - lastPageUsed));
  contentEl.style.setProperty('--pg-fill', lastMissing + 'px');
}

/** Přečte základní vizuální mezeru mezi listy z proměnné --pg-gap (px). */
function getBaseGapPx(page: HTMLElement): number {
  const raw = getComputedStyle(page).getPropertyValue('--pg-gap').trim();
  const n = parseFloat(raw);
  return isNaN(n) ? 16 : n;
}

/** Sestaví DecorationSet z pozic řezů. */
function buildDecorations(view: EditorView, breaks: PageBreakInfo[]): DecorationSet {
  const decos: Decoration[] = [];
  for (const brk of breaks) {
    const widget = Decoration.widget(
      brk.pos,
      () => {
        const gap = document.createElement('div');
        // Popisek „Konec stránky" nese samotný pageBreak node (původní čára);
        // gap je jen vizuální mezera + dorovnání předchozí stránky na A4.
        gap.className = brk.manual ? 'pm-page-gap pm-page-gap-manual' : 'pm-page-gap';
        gap.contentEditable = 'false';
        gap.setAttribute('data-page-gap', '');
        return gap;
      },
      { side: -1, key: (brk.manual ? 'mgap-' : 'gap-') + brk.pos },
    );
    decos.push(widget);
  }
  return DecorationSet.create(view.state.doc, decos);
}

export const LivePagination = Extension.create({
  name: 'livePagination',

  addProseMirrorPlugins() {
    let decoSet = DecorationSet.empty;
    let timer: number | null = null;
    let lastKey = '';

    const schedule = (view: EditorView) => {
      if (timer !== null) window.clearTimeout(timer);
      // Debounce kvůli plynulosti psaní — přepočet až po odmlce.
      timer = window.setTimeout(() => {
        timer = null;
        recalc(view);
      }, 220);
    };

    const recalc = (view: EditorView) => {
      try {
        const ps = store.get().doc.pageSetup;
        const breaks = computeBreaks(view, ps);
        const key = breaks.map((b) => (b.manual ? 'm' : 'a') + b.pos).join(',');
        if (key !== lastKey) {
          lastKey = key;
          decoSet = buildDecorations(view, breaks);
          view.dispatch(view.state.tr.setMeta(paginationKey, { refresh: true }));
        }
        // Dorovnání poslední stránky na plnou A4 vždy po layoutu (i když se řezy
        // nezměnily — obsah poslední stránky se mohl změnit). rAF zajistí, že
        // měříme až po vykreslení dekorací.
        requestAnimationFrame(() => {
          try { applyPageFills(view, ps); } catch { /* měření selhalo — ignoruj */ }
        });
      } catch {
        // Stránkování je jen vizuální vrstva; jeho chyba nikdy nesmí shodit
        // editor. Když přepočet selže, dokument zůstane funkční bez řezů.
      }
    };

    return [
      new Plugin({
        key: paginationKey,
        state: {
          init: () => DecorationSet.empty,
          apply: (tr) => {
            // Když jde o náš refresh meta, vrať aktuální decoSet.
            if (tr.getMeta(paginationKey)) return decoSet;
            // Jinak mapuj staré dekorace přes změny dokumentu.
            return decoSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
        view(view) {
          // ResizeObserver sleduje ZMĚNU ŠÍŘKY editoru (zoom, okno). Výšku
          // ignorujeme — tu mění naše vlastní výplň (--pg-fill) a reagovat na
          // ni by vytvořilo nekonečnou smyčku fill → resize → fill.
          let lastWidth = 0;
          const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width ?? 0;
            if (Math.abs(w - lastWidth) > 0.5) {
              lastWidth = w;
              schedule(view);
            }
          });
          ro.observe(view.dom as HTMLElement);

          // Prvotní výpočet ve více vlnách — layout a fonty mohou dobíhat.
          // Vždy přes setTimeout/rAF, NIKDY synchronně: dispatch během
          // inicializace view by shodil ProseMirror.
          window.setTimeout(() => recalc(view), 0);
          window.setTimeout(() => recalc(view), 160);
          window.setTimeout(() => recalc(view), 450);
          if ((document as any).fonts && (document as any).fonts.ready) {
            (document as any).fonts.ready.then(() => recalc(view)).catch(() => {});
          }

          // Změny store (page setup, header/footer) → přepočet. subscribe volá
          // listener OKAMŽITĚ; první (synchronní) volání ignorujeme, aby
          // nedošlo k dispatchi během inicializace.
          let firstStoreCall = true;
          const unsub = store.subscribe(() => {
            if (firstStoreCall) { firstStoreCall = false; return; }
            schedule(view);
          });

          return {
            update: (v, prevState) => {
              // Náš vlastní refresh (meta) nepřepočítáváme znovu — jen změny
              // dokumentu nebo velikosti spouštějí nové stránkování.
              if (v.state.doc.eq(prevState.doc)) {
                // Dokument beze změny (jen selekce/meta) → nepřepočítávej.
                return;
              }
              schedule(v);
            },
            destroy: () => {
              ro.disconnect();
              unsub();
              if (timer !== null) window.clearTimeout(timer);
            },
          };
        },
      }),
    ];
  },
});
